import React, { Fragment, useEffect, useState } from "react";
import Table from "react-bootstrap/Table";
import { UserLink } from "../util/Links";
import { ErrorCheckMark, SuccessCheckMark } from "./AxisCheckMark";
import { AxisDateTimeFull } from "../util/AxisDateTime";
import { PaginationControl } from "react-bootstrap-pagination-control";
import UserMessageModal from "../modals/UserMessageModal";
import { useAuth } from "../context/AuthContext";
import AxisSpinner from "./AxisSpinner";

export const MessageTable = ({
  refresh = null,
  selectedFetchCount = 25,
  selectedType = null,
  selectedUser = null,
  markSeen = false,
}) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [offset, setOffset] = useState(0);
  const [totalMessages, setTotalMessages] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [fetchCount, setFetchCount] = useState(selectedFetchCount);
  const [showMessage, setShowMessage] = useState(false);
  const [messageData, setMessageData] = useState(null);
  const [sortKey, setSortKey] = useState("created");
  const [sortDirection, setSortDirection] = useState("dsc");
  const [user, setUser] = useState(selectedUser);
  const [type, setType] = useState(selectedType);

  useEffect(() => {
    setOffset(0);
    setDataSource([]);
    setTotalMessages(0);
    setTotalPages(1);
    setUser(selectedUser);
    setType(selectedType);
    setFetchCount(selectedFetchCount);
  }, [selectedUser, selectedType, selectedFetchCount]);

  useEffect(() => {
    paginate();
  }, [offset, sortKey, sortDirection, fetchCount, user, type, refresh]);

  const paginate = async (e) => {
    try {
      setLoading(true);

      var params = {};
      if (user !== null && user !== undefined) {
        params["user_id"] = user?.id;
      }
      if (type !== null && type !== undefined) {
        params["type"] = type;
      }

      const pageSettings = {
        direction: sortDirection,
        key: sortKey,
        page: offset,
        elementPerPage: fetchCount,
      };
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: params,
      };

      const page = await authAxios().post(
        "/api/backoffice/messages/paging",
        pageSettings,
        config,
      );
      setLoading(false);

      if (page.data.content.length === 0) {
        if (dataSource.length === 0) {
          setTotalMessages(0);
        }
        console.log("empty api call");
        setDataSource([]);
        return;
      }

      setDataSource(page.data.content);
      setTotalMessages(page.data.totalElements);
      var length = Math.ceil(page.data.totalElements / fetchCount);
      setTotalPages(length);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
      setDataSource([]);
      setTotalMessages(0);
    }
  };

  const markMessageSeen = async (message) => {
    try {
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {
          message_id: message?.id,
        },
      };

      await authAxios().post("/api/user/message_seen", null, config);
    } catch (error) {
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const handleMessageOpen = async (message) => {
    if (markSeen) {
      markMessageSeen(message);
    }
    setMessageData(message);
    setShowMessage(true);
  };

  const handleSetSortKey = (key) => {
    if (sortKey === key) {
      if (sortDirection === "asc") {
        setSortDirection("dsc");
      } else {
        setSortDirection("asc");
      }
      return;
    }
    setSortKey(key);
  };

  const tableHeading = (key, name) => {
    if (key === sortKey) {
      return sortDirection === "dsc" ? name + " ↓" : name + " ↑";
    }
    return name;
  };

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;

    return (
      <Fragment>
        <Table striped bordered hover>
          <thead>
            <tr style={{ cursor: "pointer" }}>
              <th onClick={() => handleSetSortKey("user")}>
                {tableHeading("user", "Recipiant")}
              </th>
              <th onClick={() => handleSetSortKey("sender")}>
                {tableHeading("sender", "Sender")}
              </th>
              <th onClick={() => handleSetSortKey("type")}>
                {tableHeading("type", "Type")}
              </th>
              <th onClick={() => handleSetSortKey("title")}>
                {tableHeading("title", "Title")}
              </th>
              <th onClick={() => handleSetSortKey("isPush")}>
                {tableHeading("isPush", "Push")}
              </th>
              <th onClick={() => handleSetSortKey("seen")}>
                {tableHeading("seen", "Seen")}
              </th>
              <th onClick={() => handleSetSortKey("expires")}>
                {tableHeading("expires", "Expires")}
              </th>
              <th onClick={() => handleSetSortKey("created")}>
                {tableHeading("created", "Created")}
              </th>
            </tr>
          </thead>
          <tbody>
            {dataSource.map((message) => (
              <tr key={message?.id}>
                <td>{UserLink(message?.user)}</td>
                <td>{UserLink(message?.sender)}</td>
                <td>{message?.type}</td>
                <td
                  className={["pointer text-info"]}
                  onClick={() => handleMessageOpen(message)}
                >
                  {message?.title}
                </td>
                <td>
                  {message?.push === true ? (
                    <SuccessCheckMark />
                  ) : (
                    <ErrorCheckMark />
                  )}
                </td>
                <td>
                  {message?.seen === true ? (
                    <SuccessCheckMark />
                  ) : (
                    <ErrorCheckMark />
                  )}
                </td>
                <td>{AxisDateTimeFull(message?.expires)}</td>
                <td>{AxisDateTimeFull(message?.created)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div>
          <p>
            Page {offset + 1} of {totalPages}, showing {dataSource?.length}{" "}
            records(s) out of {totalMessages}
          </p>
          <div>
            <PaginationControl
              page={offset + 1}
              between={3}
              total={totalMessages}
              limit={fetchCount}
              changePage={(page) => {
                setOffset(page - 1);
              }}
              ellipsis={1}
            />
          </div>
        </div>

        <UserMessageModal
          visible={showMessage}
          close={() => {
            setShowMessage(false);
            if (markSeen) {
              paginate();
            }
          }}
          message={messageData}
        />
      </Fragment>
    );
  };
  return SpinnerOrData();
};
